import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { agreementApi, vehicleApi, emailApi } from "@/services/api";
import { downloadAgreementPDF, printAgreementPDF } from "@/utils/pdfGenerator";
import { AppHeader } from "@/components/layout/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  FileX,
  Loader2,
  Edit2,
  Save,
  X,
  Printer,
  Download,
  Mail,
} from "lucide-react";
import { toast } from "sonner";

// Company logo URL
const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_rentflow-67/artifacts/zjs6jlz1_ChatGPT%20Image%20Feb%201%2C%202026%2C%2007_47_29%20PM.png";

// Import refactored section components
import {
  RenterInfoSection,
  VehicleInfoSection,
  RentalPeriodSection,
  RentalTermsSection,
  CreditCardSection,
  InsuranceSection,
  DamageChecklistSection,
  SignatureSection,
  LegalWaiverSection,
} from "@/components/agreement";

const statusStyles = {
  active: "bg-success-light text-success",
  completed: "bg-primary/10 text-primary",
  cancelled: "bg-destructive-light text-destructive"
};

export default function AgreementPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(searchParams.get('edit') === 'true');
  
  // Form state
  const [companyInfo, setCompanyInfo] = useState({});
  const [renterInfo, setRenterInfo] = useState({});
  const [vehicleInfo, setVehicleInfo] = useState({});
  const [rentalPeriod, setRentalPeriod] = useState({});
  const [rentalTerms, setRentalTerms] = useState({});
  const [agreementInfo, setAgreementInfo] = useState({});
  const [creditCardInfo, setCreditCardInfo] = useState({});
  const [insuranceInfo, setInsuranceInfo] = useState({});
  const [signatureInfo, setSignatureInfo] = useState({ 
    renterSignature: "", renterSignedAt: "", 
    agentSignature: "", agentSignedAt: "", agentName: "",
    waiverAccepted: false, waiverAcceptedAt: "", waiverInitials: ""
  });
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [damageChecklist, setDamageChecklist] = useState({ 
    items: [], additionalNotes: "", inspectedBy: "", inspectionDate: "" 
  });
  const [fleetVehicles, setFleetVehicles] = useState([]);
  const [notes, setNotes] = useState("");
  
  // Email dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    const fetchAgreement = async () => {
      try {
        setLoading(true);
        const data = await agreementApi.getAgreement(id);
        setAgreement(data);
        setCompanyInfo(data.companyInfo || {});
        setRenterInfo(data.renterInfo || {});
        setVehicleInfo(data.vehicleInfo || {});
        setRentalPeriod(data.rentalPeriod || {});
        setRentalTerms(data.rentalTerms || {});
        setAgreementInfo(data.agreementInfo || {});
        setCreditCardInfo(data.creditCardInfo || {});
        setInsuranceInfo(data.insuranceInfo || {});
        setSignatureInfo(data.signatureInfo || { 
          renterSignature: "", renterSignedAt: "", 
          agentSignature: "", agentSignedAt: "", agentName: "",
          waiverAccepted: false, waiverAcceptedAt: "", waiverInitials: ""
        });
        setSelectedVehicleId(data.selectedVehicleId || "");
        setDamageChecklist(data.damageChecklist || { 
          items: [], additionalNotes: "", inspectedBy: "", inspectionDate: "" 
        });
        setNotes(data.notes || "");
        setEmailTo(data.renterInfo?.email || "");
        setError(null);
      } catch (err) {
        console.error('Error fetching agreement:', err);
        setError('Agreement not found');
      } finally {
        setLoading(false);
      }
    };

    const fetchFleetVehicles = async () => {
      try {
        const vehicles = await vehicleApi.getAllVehicles();
        setFleetVehicles(vehicles);
      } catch (err) {
        console.error('Error fetching fleet vehicles:', err);
      }
    };

    fetchAgreement();
    fetchFleetVehicles();
  }, [id]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
            <p className="text-muted-foreground">Loading agreement...</p>
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (error || !agreement) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <FileX className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">Agreement Not Found</h1>
            <p className="text-muted-foreground mb-6">The agreement you&apos;re looking for doesn&apos;t exist.</p>
            <Button onClick={() => navigate('/agreements')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Agreements
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const handleSave = async () => {
    try {
      await agreementApi.updateAgreement(id, {
        companyInfo, renterInfo, vehicleInfo, rentalPeriod, rentalTerms,
        agreementInfo, creditCardInfo, insuranceInfo, signatureInfo,
        selectedVehicleId, damageChecklist, notes
      });
      
      setAgreement(prev => ({
        ...prev, companyInfo, renterInfo, vehicleInfo, rentalPeriod, rentalTerms,
        agreementInfo, creditCardInfo, insuranceInfo, signatureInfo,
        selectedVehicleId, damageChecklist, notes
      }));
      
      setIsEditing(false);
      toast.success("Agreement Saved", {
        description: `Agreement #${agreementInfo.number} has been saved.`
      });
    } catch (error) {
      toast.error("Failed to save agreement");
    }
  };

  const handleCancel = () => {
    setCompanyInfo(agreement.companyInfo || {});
    setRenterInfo(agreement.renterInfo || {});
    setVehicleInfo(agreement.vehicleInfo || {});
    setRentalPeriod(agreement.rentalPeriod || {});
    setRentalTerms(agreement.rentalTerms || {});
    setAgreementInfo(agreement.agreementInfo || {});
    setCreditCardInfo(agreement.creditCardInfo || {});
    setInsuranceInfo(agreement.insuranceInfo || {});
    setSignatureInfo(agreement.signatureInfo || { 
      renterSignature: "", renterSignedAt: "", 
      agentSignature: "", agentSignedAt: "", agentName: "",
      waiverAccepted: false, waiverAcceptedAt: "", waiverInitials: ""
    });
    setSelectedVehicleId(agreement.selectedVehicleId || "");
    setDamageChecklist(agreement.damageChecklist || { 
      items: [], additionalNotes: "", inspectedBy: "", inspectionDate: "" 
    });
    setNotes(agreement.notes || "");
    setIsEditing(false);
  };

  const handleSelectFleetVehicle = (vehicleId) => {
    setSelectedVehicleId(vehicleId);
    const selectedVehicle = fleetVehicles.find(v => v.id === vehicleId);
    if (selectedVehicle) {
      setVehicleInfo({
        ...vehicleInfo,
        make: selectedVehicle.make,
        model: selectedVehicle.model,
        year: selectedVehicle.year,
        color: selectedVehicle.color,
        licensePlate: selectedVehicle.licensePlate,
        vin: selectedVehicle.vin
      });
      setRentalTerms({
        ...rentalTerms,
        dailyRate: selectedVehicle.dailyRate || rentalTerms.dailyRate
      });
    }
  };

  const handlePrint = () => printAgreementPDF(agreement);

  const handleSendEmail = async () => {
    if (!emailTo) {
      toast.error("Please enter an email address");
      return;
    }
    
    try {
      setSendingEmail(true);
      await emailApi.sendAgreement(id, {
        to_email: emailTo,
        to_name: renterInfo?.name || "",
        message: emailMessage
      });
      toast.success("Agreement Sent!", {
        description: `Rental agreement emailed to ${emailTo}`
      });
      setEmailDialogOpen(false);
      setEmailMessage("");
    } catch (error) {
      toast.error("Failed to send email", {
        description: error.response?.data?.detail || "Please try again"
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="no-print">
        <AppHeader />
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation & Actions */}
        <div className="no-print mb-6">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/agreements')} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Agreements
            </Button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <Button onClick={handleSave} className="gap-2 bg-success hover:bg-success/90 text-success-foreground">
                    <Save className="w-4 h-4" />
                    Save Agreement
                  </Button>
                  <Button variant="outline" onClick={handleCancel} className="gap-2">
                    <X className="w-4 h-4" />
                    Cancel
                  </Button>
                </>
              ) : (
                <Button variant="outline" onClick={() => setIsEditing(true)} className="gap-2">
                  <Edit2 className="w-4 h-4" />
                  Edit Agreement
                </Button>
              )}
            </div>
            {!isEditing && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handlePrint} className="gap-2">
                  <Printer className="w-4 h-4" />
                  Print
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadAgreementPDF(agreement)} className="gap-2">
                  <Download className="w-4 h-4" />
                  PDF
                </Button>
                <Button size="sm" onClick={() => setEmailDialogOpen(true)} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="send-agreement-email-btn">
                  <Mail className="w-4 h-4" />
                  Email
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Email Dialog */}
        <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Send Agreement via Email
              </DialogTitle>
              <DialogDescription>
                Send rental agreement #{agreementInfo.number} to the renter
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="agreement-email">Recipient Email</Label>
                <Input
                  id="agreement-email"
                  type="email"
                  placeholder="renter@example.com"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  data-testid="agreement-email-to-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="agreement-message">Message (Optional)</Label>
                <Textarea
                  id="agreement-message"
                  placeholder="Add a personal message to include in the email..."
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendEmail} disabled={sendingEmail} data-testid="send-agreement-confirm-btn">
                {sendingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Agreement
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Agreement Document */}
        <div className="bg-card rounded-lg shadow-invoice">
          {/* Header */}
          <div className="invoice-header">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <img 
                  src={COMPANY_LOGO} 
                  alt="Ak Car Rental" 
                  className="h-14 w-auto object-contain"
                />
                <div>
                  {isEditing ? (
                    <Input
                      value={companyInfo.name || ''}
                      onChange={(e) => setCompanyInfo({...companyInfo, name: e.target.value})}
                      className="text-xl font-bold bg-primary-foreground/10 border-primary-foreground/30 text-primary-foreground h-auto py-1"
                    />
                  ) : (
                    <h1 className="text-xl sm:text-2xl font-bold text-primary-foreground">{companyInfo.name}</h1>
                  )}
                  <p className="text-primary-foreground/70 text-sm">{companyInfo.website}</p>
                </div>
              </div>
              <div className="flex flex-col items-start sm:items-end gap-2">
                <span className="text-2xl sm:text-3xl font-bold text-primary-foreground">RENTAL AGREEMENT</span>
                <div className="flex items-center gap-3">
                  <span className="text-primary-foreground/90 font-mono text-sm">#{agreementInfo.number}</span>
                  {isEditing ? (
                    <Select 
                      value={agreementInfo.status || 'active'} 
                      onValueChange={(v) => setAgreementInfo({...agreementInfo, status: v})}
                    >
                      <SelectTrigger className="w-[140px] h-8 bg-primary-foreground/20 border-primary-foreground/30 text-primary-foreground" data-testid="status-selector">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusStyles[agreementInfo.status]}`}>
                      {agreementInfo.status?.toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 sm:p-8 space-y-8">
            {/* Renter & Vehicle Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <RenterInfoSection 
                renterInfo={renterInfo} 
                setRenterInfo={setRenterInfo} 
                isEditing={isEditing} 
              />
              <VehicleInfoSection 
                vehicleInfo={vehicleInfo}
                setVehicleInfo={setVehicleInfo}
                isEditing={isEditing}
                fleetVehicles={fleetVehicles}
                selectedVehicleId={selectedVehicleId}
                onSelectFleetVehicle={handleSelectFleetVehicle}
              />
            </div>

            {/* Rental Period */}
            <RentalPeriodSection 
              rentalPeriod={rentalPeriod}
              setRentalPeriod={setRentalPeriod}
              isEditing={isEditing}
            />

            {/* Rental Terms */}
            <RentalTermsSection 
              rentalTerms={rentalTerms}
              setRentalTerms={setRentalTerms}
              isEditing={isEditing}
            />

            {/* Credit Card Information */}
            <CreditCardSection 
              creditCardInfo={creditCardInfo}
              setCreditCardInfo={setCreditCardInfo}
              isEditing={isEditing}
            />

            {/* Insurance Information */}
            <InsuranceSection 
              insuranceInfo={insuranceInfo}
              setInsuranceInfo={setInsuranceInfo}
              isEditing={isEditing}
            />

            {/* Vehicle Damage Checklist */}
            <DamageChecklistSection 
              damageChecklist={damageChecklist}
              setDamageChecklist={setDamageChecklist}
              isEditing={isEditing}
            />

            {/* Agreement Date & Status */}
            <div className="flex flex-wrap gap-6 p-4 bg-muted/30 rounded-lg border border-border">
              <div>
                <Label className="text-xs text-muted-foreground uppercase">Agreement Date</Label>
                {isEditing ? (
                  <Input 
                    type="date" 
                    value={agreementInfo.date || ''} 
                    onChange={(e) => setAgreementInfo({...agreementInfo, date: e.target.value})} 
                    className="h-8 text-sm w-36 mt-1" 
                  />
                ) : (
                  <p className="text-sm font-medium">{formatDate(agreementInfo.date)}</p>
                )}
              </div>
              {isEditing && (
                <div>
                  <Label className="text-xs text-muted-foreground uppercase">Status</Label>
                  <Select value={agreementInfo.status} onValueChange={(v) => setAgreementInfo({...agreementInfo, status: v})}>
                    <SelectTrigger className="h-8 w-32 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <Label className="text-xs text-muted-foreground uppercase">Notes / Special Conditions</Label>
              {isEditing ? (
                <Textarea 
                  value={notes} 
                  onChange={(e) => setNotes(e.target.value)} 
                  placeholder="Enter any special notes or conditions..." 
                  className="mt-2" 
                  rows={3} 
                />
              ) : (
                <p className="text-sm text-muted-foreground mt-2">{notes || 'No additional notes.'}</p>
              )}
            </div>

            {/* Legal Waiver Section */}
            <LegalWaiverSection 
              waiverAccepted={signatureInfo.waiverAccepted}
              setWaiverAccepted={(accepted) => setSignatureInfo({
                ...signatureInfo, 
                waiverAccepted: accepted,
                waiverAcceptedAt: accepted ? new Date().toISOString() : ""
              })}
              waiverInitials={signatureInfo.waiverInitials}
              setWaiverInitials={(initials) => setSignatureInfo({
                ...signatureInfo,
                waiverInitials: initials
              })}
              isEditing={isEditing}
            />

            {/* Signature Section */}
            <SignatureSection 
              signatureInfo={signatureInfo}
              setSignatureInfo={setSignatureInfo}
              renterName={renterInfo?.name}
              isEditing={isEditing}
            />

            {/* Footer */}
            <Separator />
            <div className="text-center space-y-2">
              <p className="text-xs text-muted-foreground">
                {companyInfo?.name || 'Ak Car Rental'} • {companyInfo?.phone} • {companyInfo?.email}
              </p>
              <p className="text-xs text-muted-foreground">
                {companyInfo?.address}, {companyInfo?.city}, {companyInfo?.province} {companyInfo?.postalCode}
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
